import { Trans } from '@lingui/macro'

import * as React from 'react'

import { isDefined } from '~logic/helpers/isDefined'
import { Box } from '~ui/components/layout/Box'
import { Stack } from '~ui/components/layout/Stack'
import { Skeleton } from '~ui/components/loadingSkeletons/Skeleton'
import { Text } from '~ui/components/typography/Text/Text'
import { AllergenList } from '~ui/components/ware/Allergens/AllergenList'
import { TSingleSpace } from '~ui/styleGuide/spaces'

import { AgeControlBadge } from '../Badges/AgeControlBadge'
import { DetailInfoHeader } from './DetailInfoHeader'

type TProductHeaderProps = Omit<PropsOf<typeof DetailInfoHeader>, 'gridColumn'>

type TBoxProps = PropsOf<typeof Box>

type TProps = NoChildren &
  SkeletonProps &
  TProductHeaderProps & {
    /**
     * product description
     */
    descriptionLabel?: Nullable<string>
    /**
     * array of product's allergens with id, nameLabel (BE code) and allergen number (BE category)
     */
    allergens?: PropsOf<typeof AllergenList>['allergens']
    /**
     * a boolean value to determine whether the product is intender only for people 18+
     */
    ageControl?: boolean
    /**
     * horizontal padding of the content
     */
    horizontalPadding?: TSingleSpace
    /**
     * `grid-column` of the wrapper containing description label and allergen list
     */
    descriptionGridColumn?: TBoxProps['gridColumn']
    /**
     * css `order` of the wrapper containing description label and allergen list
     */
    descriptionOrder?: TBoxProps['order']
  }

/**
 * Renders ProductHeader (positioned heading), description and allergens of the product.
 * Can show loading skeletons (`showSkeleton`) instead of the descriptionLabel and allergen list.
 */
export const DetailInfo: React.FC<TProps> = ({
  descriptionLabel,
  allergens = [],
  showSkeleton = false,
  descriptionGridColumn,
  descriptionOrder,
  ageControl,
  horizontalPadding = `none`,
  ...productHeaderProps
}) => {
  const hasDescription = isDefined(descriptionLabel) && descriptionLabel.trim().length > 0
  const hasAllergens = Boolean(allergens) && allergens.length > 0

  return (
    <>
      {/* product header is not wrapped because of the sticky behavior that do not work otherwise */}
      <DetailInfoHeader {...productHeaderProps} horizontalPadding={horizontalPadding} />

      <Box gridColumn={descriptionGridColumn} order={descriptionOrder}>
        <Stack
          spacing={`smallMedium`}
          padding={{ vertical: `none`, horizontal: horizontalPadding }}
        >
          {showSkeleton && (
            <>
              <Skeleton showSkeleton>
                <Text>description placeholder</Text>
              </Skeleton>

              <Skeleton showSkeleton maxWidth={`8rem`}>
                <Text fontSize={`s14`}>allergens placeholder</Text>
              </Skeleton>
            </>
          )}

          {!showSkeleton && (
            <>
              {hasDescription && <Text>{descriptionLabel}</Text>}

              {hasAllergens && (
                <AllergenList
                  label={<Trans>allergens</Trans>}
                  allergens={allergens}
                  fontSize={`s14`}
                />
              )}

              {ageControl && (
                <Box alignSelf={`flex-start`}>
                  <AgeControlBadge />
                </Box>
              )}
            </>
          )}
        </Stack>
      </Box>
    </>
  )
}
